import styled from "styled-components";
import { color, font } from "../../theme/tokens";

/**
 * A hairline-ruled index rather than a field of floating drop-shadow cards.
 * Negative margins + per-cell borders give a continuous grid whose outer
 * edge stays flush with the band gutter.
 */
export const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  /* Border on the container, not just top/left, so a partial final row still
     closes against the outer edge at every column count. */
  border: 1px solid ${color.rule};

  @media screen and (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media screen and (max-width: 760px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const GridItem = styled.a`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  text-decoration: none;
  border-right: 1px solid ${color.rule};
  border-bottom: 1px solid ${color.rule};
  padding: 36px 28px 32px;
  min-height: 208px;
  background: transparent;
  transition: background 200ms ease;
  cursor: ${({ $static }) => ($static ? 'default' : 'pointer')};

  &:hover {
    background: ${({ $static }) => ($static ? 'transparent' : color.greenTint)};
  }

  @media screen and (max-width: 600px) {
    padding: 26px 20px 24px;
    min-height: 176px;
  }

  @media screen and (max-width: 400px) {
    padding: 22px 14px 20px;
  }
`;

/**
 * Every logo is pre-normalized (see scripts/normalize_logos.py): autocropped to
 * its mark, recoloured to a single ink, transparent background. So this just
 * needs a fixed slot — no blend modes or per-logo filters.
 */
export const ItemImage = styled.img`
  width: auto;
  max-width: 66%;
  height: 40px;
  object-fit: contain;
  object-position: left center;
  opacity: 0.78;
  transition: opacity 200ms ease;

  ${GridItem}:hover & {
    opacity: 1;
  }
`;

/**
 * Reserves the logo slot for companies whose mark we don't have yet, so their
 * name and sector still baseline-align with the tiles either side. Deliberately
 * empty rather than repeating the name — the title below already carries it.
 */
export const ItemLogoSlot = styled.span`
  display: block;
  height: 40px;
`;

export const ItemTitle = styled.h3`
  font-family: ${font.display};
  color: ${color.inkStrong};
  font-size: 21px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin: 28px 0 6px;
  transition: color 200ms ease;

  ${GridItem}:hover & {
    color: ${color.green};
  }
`;

export const ItemCategory = styled.span`
  font-family: ${font.body};
  color: ${color.inkMuted};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  line-height: 1.4;
  /* Categories are single long words ("MANUFACTURING"); at two columns on a
     320px screen the tracking pushes them past the cell. */
  max-width: 100%;
  overflow-wrap: anywhere;

  @media screen and (max-width: 400px) {
    font-size: 10px;
    letter-spacing: 0.07em;
  }
`;
